import React, { useEffect, useState, useMemo } from "react";
import { clientApi } from "../lib/api";
import { TrendingUp, TrendingDown, DollarSign, Filter } from "lucide-react";

const fmtBRL = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);
const fmtInt = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);

const KINDS = {
  payment: { label: "Pagamento", color: "text-green-400" },
  due: { label: "A pagar", color: "text-red-400" },
  invested: { label: "Investimento", color: "text-accent-orange" },
  leads: { label: "Leads", color: "text-blue-400" },
  units_sold: { label: "Unidades Vendidas", color: "text-accent-yellow" },
  cpl: { label: "CPL", color: "text-purple-400" },
};

export default function Financeiro() {
  const [fins, setFins] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [kind, setKind] = useState("");

  useEffect(() => {
    Promise.all([clientApi.get("/products"), clientApi.get("/campaigns")]).then(([p, c]) => {
      setProducts(p.data); setCampaigns(c.data);
    });
  }, []);

  useEffect(() => {
    const params = {};
    if (campaignId) params.campaign_id = campaignId;
    clientApi.get("/financials", { params }).then(({ data }) => setFins(data));
  }, [campaignId]);

  const filtered = useMemo(() => kind ? fins.filter((f) => f.kind === kind) : fins, [fins, kind]);

  const totals = useMemo(() => {
    const acc = {};
    for (const f of fins) {
      acc[f.kind] = (acc[f.kind] || 0) + (Number(f.amount) || 0);
    }
    return acc;
  }, [fins]);

  return (
    <div className="space-y-6" data-testid="cliente-financeiro-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-accent-orange mb-1">Financeiro</div>
        <h1 className="font-display text-3xl md:text-4xl">Extrato & Investimentos</h1>
      </div>

      {/* Filtros */}
      <div className="grid md:grid-cols-2 gap-3 border border-white/10 rounded-xl p-4 bg-white/[0.02]">
        <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm">
          <option value="">Todas as campanhas</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm">
          <option value="">Todos os tipos</option>
          {Object.entries(KINDS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(KINDS).map(([k, meta]) => (
          <div key={k} className="border border-white/10 rounded-xl p-4 bg-white/[0.02]">
            <div className={`text-[10px] uppercase tracking-widest ${meta.color} mb-1`}>{meta.label}</div>
            <div className="font-display text-xl">
              {["leads", "units_sold"].includes(k) ? fmtInt(totals[k]) : fmtBRL(totals[k])}
            </div>
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="financeiro-table">
          <thead className="bg-black border-b border-white/10">
            <tr>
              <th className="text-left p-3 text-[10px] uppercase tracking-widest text-neutral-500">Data</th>
              <th className="text-left p-3 text-[10px] uppercase tracking-widest text-neutral-500">Tipo</th>
              <th className="text-left p-3 text-[10px] uppercase tracking-widest text-neutral-500">Subcategoria</th>
              <th className="text-left p-3 text-[10px] uppercase tracking-widest text-neutral-500">Descrição</th>
              <th className="text-right p-3 text-[10px] uppercase tracking-widest text-neutral-500">Valor</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => (
              <tr key={f.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="p-3 text-neutral-400">{f.date ? new Date(f.date).toLocaleDateString("pt-BR") : "—"}</td>
                <td className={`p-3 font-semibold ${KINDS[f.kind]?.color || ""}`}>{KINDS[f.kind]?.label || f.kind}</td>
                <td className="p-3 text-neutral-500">{f.subcategory || "—"}</td>
                <td className="p-3 text-neutral-300">{f.note || "—"}</td>
                <td className="p-3 text-right font-mono">
                  {["leads", "units_sold"].includes(f.kind) ? fmtInt(f.amount) : fmtBRL(f.amount)}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-neutral-500">Nenhum registro.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
